import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static values = {
    message: { type: String, default: "Please fill out this field." },
    unlessFilled: { type: String, default: "" },
    requiredWhenFilled: { type: String, default: "" },
    required: { type: Boolean, default: true }
  }

  connect() {
    this.parentForm = this.element.closest("form")
    if (!this.parentForm) return

    this.boundValidate = this.validate.bind(this)
    this.parentForm.addEventListener("submit", this.boundValidate)

    // Bound on the form rather than the input so that filling the field this
    // one defers to also clears the error, which would otherwise sit on a
    // field that no longer needs a value.
    this.boundClearError = this.clearErrorFor.bind(this)
    this.parentForm.addEventListener("input", this.boundClearError)
  }

  disconnect() {
    if (this.parentForm && this.boundValidate) {
      this.parentForm.removeEventListener("submit", this.boundValidate)
    }
    if (this.parentForm && this.boundClearError) {
      this.parentForm.removeEventListener("input", this.boundClearError)
    }
    this.removeError()
  }

  // Switching to an alert type that does not need this field must not leave a
  // stale error behind on a field that is now optional.
  requiredValueChanged(isRequired) {
    if (!isRequired && !this.requiredByCompanionField()) this.clearError()
  }

  // Typing into a companion field can make this one required rather than
  // satisfied, so re-check instead of clearing unconditionally.
  clearErrorFor(event) {
    if (event.target === this.element) {
      this.clearError()
      return
    }

    if (!this.defersTo(event.target)) return

    if (this.requiredValue || this.requiredByCompanionField()) {
      if (this.element.value.trim() === "" && !this.satisfiedByOtherField()) return
    }

    this.clearError()
  }

  validate(event) {
    if (!this.requiredValue && !this.requiredByCompanionField()) return
    if (this.element.closest(".hidden")) return
    if (this.element.value.trim() !== "") return
    if (this.satisfiedByOtherField()) return

    event.preventDefault()
    // Keeps @rails/ujs's document-level submit handler from disabling the
    // submit button on a cancelled submission.
    event.stopPropagation()

    this.showError()
  }

  showError() {
    const formGroup = this.formGroup()
    if (formGroup) formGroup.classList.add("has-error")

    // Look the element up in the DOM rather than trusting the instance
    // reference, so a controller that reconnects over markup already showing
    // an error reuses it instead of appending a second copy.
    this.errorElement = this.existingError()

    if (!this.errorElement) {
      this.errorElement = document.createElement("div")
      this.errorElement.className = "input-required-error"
      this.errorElement.setAttribute("role", "alert")
      this.errorElement.textContent = this.messageValue

      if (formGroup) {
        formGroup.appendChild(this.errorElement)
      } else {
        this.element.insertAdjacentElement("afterend", this.errorElement)
      }
    }

    if (typeof this.element.scrollIntoView === "function") {
      this.element.scrollIntoView({ block: "center" })
    }
  }

  existingError() {
    const formGroup = this.formGroup()
    if (formGroup) return formGroup.querySelector(".input-required-error")

    const next = this.element.nextElementSibling
    return next && next.classList.contains("input-required-error") ? next : null
  }

  clearError() {
    const formGroup = this.formGroup()
    if (formGroup) formGroup.classList.remove("has-error")

    this.removeError()
  }

  removeError() {
    const existing = this.errorElement || this.existingError()
    if (existing) existing.remove()
    this.errorElement = null
  }

  formGroup() {
    return this.element.closest(".form-group")
  }

  satisfiedByOtherField() {
    const other = this.deferredField()
    return Boolean(other && other.value.trim() !== "")
  }

  // Companion fields that the server requires as a set: filling either one
  // makes the rest required too, and clearing them all makes the set optional
  // again.
  requiredByCompanionField() {
    const companion = this.fieldMatching(this.requiredWhenFilledValue)
    return Boolean(companion && companion.value.trim() !== "")
  }

  defersTo(element) {
    return Boolean(element && (element === this.deferredField() || element === this.companionField()))
  }

  deferredField() {
    return this.fieldMatching(this.unlessFilledValue)
  }

  companionField() {
    return this.fieldMatching(this.requiredWhenFilledValue)
  }

  fieldMatching(selector) {
    if (!selector) return null
    if (!this.parentForm) return null

    // A malformed selector would otherwise throw out of the submit handler,
    // skipping the inline error and leaving the field silently unvalidated.
    try {
      return this.parentForm.querySelector(selector)
    } catch {
      return null
    }
  }
}
